import PubSub from "underpub-js";
import PopupPositioner from "./PopupPositioner";

/** Hover card motion: translateY on inner layers so parent transform (PopupPositioner) stays intact. */
var POPUP_ANIM_LIFT_PX = 14;
var POPUP_ANIM_IN_DURATION = "0.44s";
var POPUP_ANIM_OUT_DURATION = "0.26s";
// Smooth deceleration in; slightly quicker ease-in on out (inverse of entrance)
var POPUP_ANIM_EASE_IN = "cubic-bezier(0.19, 1, 0.22, 1)";
var POPUP_ANIM_EASE_OUT = "cubic-bezier(0.55, 0.085, 0.68, 0.53)";

// Mobile breakpoint matches the project convention (Form.scss uses 768px).
// At this width the popup flips from a hover-tooltip to a tap-toggled modal
// centered inside the map container.
var MOBILE_MEDIA_QUERY = "(max-width: 767px)";
var MODAL_FADE_IN_MS = 200;
var MODAL_FADE_OUT_MS = 200;
var mobileMQ = (typeof window !== "undefined" && typeof window.matchMedia === "function")
	? window.matchMedia(MOBILE_MEDIA_QUERY)
	: { matches: false, addEventListener: function() {}, addListener: function() {} };
function isMobile() { return mobileMQ.matches; }

// Per-instance ID for namespaced document click listeners. Multiple popups
// each attach their own, so they must not collide on the namespace.
var basePopupInstanceCounter = 0;

/**
 * BasePopup
 * Base class for popup components (PopupUnit, PopupItem, etc.)
 * Handles common functionality like floor-based visibility, hover interactions, and positioning
 */
var BasePopup = function(options) {
	this.options = {
		$el: false,
		baseClass: '',
		dotSelector: '',      // CSS selector for the dot element
		popupSelector: '',    // CSS selector for the popup element
		pointerSelector: '',  // CSS selector for the pointer element (optional)
		componentName: ''     // Component name for logging (e.g., 'PopupUnit', 'PopupItem')
	};

	$.extend(true, this.options, options);

	this.instanceId = ++basePopupInstanceCounter;

	// Cache jQuery elements
	this.$dot = null;
	this.$popup = null;
	this.$pointer = null;
	
	// Store floor index (1-based from data attribute)
	this.floorIndex = null;
	
	// Track if dot is visible for current floor
	this.isVisibleForCurrentFloor = false;
	
	// Track if event subscriptions have been set up
	this.subscriptionsSetup = false;

	/** Reposition when tooltip box grows (fonts/images); avoids wrong transformX on first measure. */
	this._popupResizeObserver = null;
	this._popupResizeRaf = null;

	// Cached map container (the closest .mol-AvailabilityMap / .mol-MapHotspots);
	// used for backdrop class toggle and to scope sibling popup queries.
	this._$mapContainerCache = null;

	// Document-level outside-tap handler installed only while a mobile modal is open.
	this._outsideHandler = null;

	// matchMedia change handler so we can clean up on dispose if ever needed.
	this._mqHandler = null;

	// Timeout id for modal fade-out finalize — cleared if another modal takes over.
	this._modalCloseTimer = null;
};

// Tracks the currently-open mobile modal across all BasePopup instances so that
// opening a second one cleanly tears down the first (class state + document listener).
BasePopup._activeModal = null;

/**
 * Initialize the popup component
 */
BasePopup.prototype.init = function() {
	var self = this;

	// Get floor index from data attribute (1-based)
	var floorIndexAttr = this.options.$el.attr('data-floor-index');
	this.floorIndex = floorIndexAttr ? parseInt(floorIndexAttr, 10) : null;
	
	// Reduced logging - only log if there's an issue
	// if (this.options.componentName) {
	// 	console.log(this.options.componentName + ': Initialized', {
	// 		floorIndexAttr: floorIndexAttr,
	// 		floorIndex: this.floorIndex,
	// 		$el: this.options.$el[0]
	// 	});
	// }
	
	if (this.floorIndex === null || isNaN(this.floorIndex)) {
		if (this.options.componentName) {
			console.warn(this.options.componentName + ': No valid floorIndex found in data-floor-index attribute', {
				attr: floorIndexAttr,
				parsed: this.floorIndex,
				element: this.options.$el[0]
			});
		}
	}

	// Find the dot and popup elements using provided selectors
	this.$dot = this.options.$el.find(this.options.dotSelector);
	this.$popup = this.options.$el.find(this.options.popupSelector);
	
	// Find pointer element if selector is provided
	if (this.options.pointerSelector) {
		this.$pointer = this.options.$el.find(this.options.pointerSelector);
	}

	// Set initial state - dot hidden, popup hidden
	this.$dot.css({
		'display': 'none',
		'opacity': '0'
	});

	this.$popup.css({
		'opacity': '0',
		'visibility': 'hidden',
		'pointer-events': 'none'
	});
	this.resetPopupMotionTransforms();

	// Disable pointer events on container initially (will be enabled when dot is shown for active floor)
	this.options.$el.css('pointer-events', 'none');

	// Wire hover (desktop) or click (mobile) interaction. Re-runs are safe — the
	// method clears its own bindings before reattaching.
	this._rebindInteraction();

	// One-time per-instance subscriptions: floor events + breakpoint flip.
	if (!this.subscriptionsSetup) {
		this.subscribeToFloorChanges();
		this._bindBreakpointListener();
		this.subscriptionsSetup = true;
	}
	
	// Request current floor in case we initialized after the initial floor was set
	this.checkCurrentFloor();
};

/**
 * Bind interaction handlers based on current breakpoint:
 * - Desktop: hover on the popup container shows / hides the floating tooltip.
 * - Mobile: tap on the dot toggles a centered modal popup.
 *
 * Idempotent — safe to call repeatedly (e.g. after a breakpoint flip).
 */
BasePopup.prototype._rebindInteraction = function() {
	var self = this;

	this.options.$el.off('mouseenter.popupHover mouseleave.popupHover click.popupTap');
	if (this.$dot && this.$dot.length) {
		this.$dot.off('click.popupTap');
	}

	if (isMobile()) {
		// Bind tap on the dot specifically. Binding on $el would re-toggle when
		// the user taps the centered modal card, since the card lives inside $el.
		var $tapTarget = (this.$dot && this.$dot.length) ? this.$dot : this.options.$el;
		$tapTarget.on('click.popupTap', function(e) {
			e.stopPropagation();
			var visible = self.$popup && self.$popup.css('visibility') === 'visible';
			if (visible) {
				self.hidePopup();
			} else {
				self.showPopup();
			}
		});
	} else {
		this.options.$el.on('mouseenter.popupHover', function(e) {
			e.stopPropagation();
			self.showPopup();
		});
		this.options.$el.on('mouseleave.popupHover', function(e) {
			e.stopPropagation();
			self.hidePopup();
		});
	}
};

/**
 * When the viewport crosses the mobile breakpoint, force-close any open popup
 * (so we don't carry mobile-only inline state into a desktop layout or vice
 * versa) and re-bind interaction handlers for the new mode.
 */
BasePopup.prototype._bindBreakpointListener = function() {
	var self = this;
	this._mqHandler = function() {
		if (BasePopup._activeModal) {
			BasePopup._activeModal.hidePopup();
		}
		self._rebindInteraction();
	};
	if (typeof mobileMQ.addEventListener === 'function') {
		mobileMQ.addEventListener('change', this._mqHandler);
	} else if (typeof mobileMQ.addListener === 'function') {
		// Older Safari fallback
		mobileMQ.addListener(this._mqHandler);
	}
};

/**
 * Closest map container; used to scope backdrop toggling and sibling-popup queries.
 */
BasePopup.prototype._mapContainer = function() {
	if (this._$mapContainerCache && this._$mapContainerCache.length) {
		return this._$mapContainerCache;
	}
	var $c = this.options.$el.closest('.mol-AvailabilityMap, .mol-MapHotspots');
	this._$mapContainerCache = $c.length ? $c : $(document.documentElement);
	return this._$mapContainerCache;
};

/**
 * Inset mobile shell (20px from map edges) — the active modal wrapper is relocated here.
 */
BasePopup.prototype._modalHost = function() {
	return this._mapContainer()
		.find('.availability-map_modal-host, .map-hotspots_modal-host')
		.first();
};

/**
 * Nodes that move for the hover finesse (card + diamond pointer). Pointer keeps rotate(45deg) from Webflow.
 */
BasePopup.prototype.getPopupMotionLayers = function() {
	if (!this.$popup || !this.$popup.length) {
		return $();
	}
	var $card = this.$popup.find('.popup-unit_card-wrapper, .popup-item_card-wrapper');
	var $ptr = this.$popup.find('.popup-unit_pointer, .popup-item_pointer');
	return $card.add($ptr);
};

BasePopup.prototype.resetPopupMotionTransforms = function() {
	var $motion = this.getPopupMotionLayers();
	if ($motion.length) {
		$motion.css({
			transform: '',
			transition: 'none'
		});
	}
};

BasePopup.prototype._detachPopupResizeObserver = function() {
	if (this._popupResizeRaf) {
		cancelAnimationFrame(this._popupResizeRaf);
		this._popupResizeRaf = null;
	}
	if (this._popupResizeObserver) {
		this._popupResizeObserver.disconnect();
		this._popupResizeObserver = null;
	}
};

/**
 * Recalculate horizontal nudge when outerWidth grows after first paint (PopupPositioner uses popupWidth).
 */
BasePopup.prototype._attachPopupResizeObserver = function() {
	var self = this;
	if (typeof ResizeObserver === 'undefined' || !this.$popup || !this.$popup.length) {
		return;
	}
	this._detachPopupResizeObserver();
	this._popupResizeObserver = new ResizeObserver(function() {
		if (!self.$popup || !self.$popup.length) {
			return;
		}
		if (self.$popup.css('visibility') !== 'visible') {
			return;
		}
		if (self._popupResizeRaf) {
			cancelAnimationFrame(self._popupResizeRaf);
		}
		self._popupResizeRaf = requestAnimationFrame(function() {
			self._popupResizeRaf = null;
			if (self.$popup && self.$popup.css('visibility') === 'visible') {
				self.adjustPopupPosition();
			}
		});
	});
	this._popupResizeObserver.observe(this.$popup[0]);
};

/**
 * Hide all other popups on the same floor
 */
BasePopup.prototype.hideOtherPopups = function() {
	var self = this;

	// Scope to the same map/hotspots instance so the global [data-floor-index] selector
	// cannot touch markers on other maps or unrelated content.
	var $scope = self.options.$el.closest('.mol-AvailabilityMap, .mol-MapHotspots');
	if (!$scope.length) {
		$scope = $(document.documentElement);
	}

	var $allPopups = $scope.find(
		'[data-floor-index="' + this.floorIndex + '"]'
	).filter('[data-amp-component="PopupUnit"], [data-amp-component="PopupItem"]');
	
	$allPopups.each(function() {
		var $otherPopup = $(this);
		
		// Skip this popup instance
		if ($otherPopup[0] === self.options.$el[0]) {
			return;
		}
		
		// Find the popup wrapper in the other popup and hide it
		var $otherPopupWrapper = $otherPopup.find('.popup-unit_wrapper, .popup-item_wrapper');
		if ($otherPopupWrapper.length) {
			$otherPopupWrapper.css({
				'opacity': '0',
				'visibility': 'hidden',
				'pointer-events': 'none',
				'transition': 'none'
			});
			$otherPopupWrapper.find(
				'.popup-unit_card-wrapper, .popup-item_card-wrapper, .popup-unit_pointer, .popup-item_pointer'
			).css({
				transform: '',
				transition: 'none'
			});
			// Reset z-index
			$otherPopup.css('z-index', '');
		}
	});
};

/**
 * ShowDot: Display the dot and fade it in
 * Changes display from none to flex, and transitions opacity from 0 to 1
 */
BasePopup.prototype.showDot = function() {
	if (!this.$dot || this.$dot.length === 0) return;

	// Set display to flex first
	this.$dot.css('display', 'flex');

	// Force reflow to ensure display change is applied
	this.$dot[0].offsetHeight;

	// Transition opacity to 1
	this.$dot.css({
		'opacity': '1',
		'transition': 'opacity 0.25s ease-out'
	});
};

/**
 * HideDot: Hide the dot and fade it out
 * Reverses the showDot interaction
 */
BasePopup.prototype.hideDot = function() {
	var self = this;

	if (!this.$dot || this.$dot.length === 0) return;

	// Transition opacity to 0
	this.$dot.css({
		'opacity': '0',
		'transition': 'opacity 0.2s ease-in'
	});

	// After transition completes, set display to none
	setTimeout(function() {
		if (self.$dot.css('opacity') === '0') {
			self.$dot.css('display', 'none');
		}
	}, 200);
};

/**
 * Adjust popup position to keep it within viewport bounds
 */
BasePopup.prototype.adjustPopupPosition = function() {
	if (!this.$popup || this.$popup.length === 0) return;
	if (!this.$dot || this.$dot.length === 0) return;

	// Use PopupPositioner utility
	var positioner = new PopupPositioner({
		$popup: this.$popup,
		$container: this.options.$el,
		margin: 10,
		debug: false
	});

	var positionResult = positioner.calculatePosition();
	if (positionResult) {
		positioner.applyPosition(positionResult);
		
		// Hide pointer if popup was adjusted horizontally (transformX is non-zero)
		// This prevents the pointer from appearing in the middle of the popup
		if (this.$pointer && this.$pointer.length > 0) {
			if (Math.abs(positionResult.transformX) > 0.1) {
				this.$pointer.css('display', 'none');
			} else {
				this.$pointer.css('display', '');
			}
		}
	}
};

/**
 * ShowPopup: Fade in the popup content (or open as a centered modal on mobile).
 */
BasePopup.prototype.showPopup = function() {
	if (!this.$popup || this.$popup.length === 0) return;
	
	// Only show popup if dot is visible for current floor
	if (!this.isVisibleForCurrentFloor) {
		return;
	}

	if (isMobile()) {
		this._showPopupAsModal();
		return;
	}

	var self = this;

	// Hide all other popups on the same floor first
	this.hideOtherPopups();

	// Object-fit pin correction runs after floor images decode; sync before measuring tooltip or pin box is wrong on first hover.
	PubSub.publish('availabilityMap:syncPinPositions');

	var lift = POPUP_ANIM_LIFT_PX + "px";
	var transitionOpacityIn =
		"opacity " + POPUP_ANIM_IN_DURATION + " " + POPUP_ANIM_EASE_IN;
	var transitionMoveIn =
		"transform " + POPUP_ANIM_IN_DURATION + " " + POPUP_ANIM_EASE_IN;

	var $card = this.$popup.find(".popup-unit_card-wrapper, .popup-item_card-wrapper");
	var $ptr = this.$popup.find(".popup-unit_pointer, .popup-item_pointer");

	// Match entrance geometry before any measure (first pass used to run with untransformed inners).
	$card.css({
		transform: "translateY(" + lift + ")",
		transition: "none"
	});
	$ptr.css({
		transform: "translateY(" + lift + ") rotate(45deg)",
		transition: "none"
	});

	// Tooltip images were lazy + visibility:hidden until hover, so decode often missed the first layout.
	this.$popup.find("img").each(function() {
		var img = this;
		if ("loading" in img && img.loading === "lazy") {
			img.loading = "eager";
		}
	});

	// First layout pass
	this.adjustPopupPosition();

	// Reposition once fonts + images have real metrics (still wrong on first hover without this).
	var fontsReady =
		typeof document !== "undefined" && document.fonts && document.fonts.ready
			? document.fonts.ready
			: Promise.resolve();
	var imageWaiters = [];
	this.$popup.find("img").each(function() {
		var img = this;
		if (!img.src && !img.srcset) {
			return;
		}
		if (img.decode && typeof img.decode === "function") {
			imageWaiters.push(
				img.decode().then(
					function() {},
					function() {}
				)
			);
		} else if (!img.complete || !img.naturalHeight) {
			imageWaiters.push(
				new Promise(function(resolve) {
					$(img).one("load error", resolve);
				})
			);
		}
	});
	var relayoutIfStillOpen = function() {
		if (
			self.$popup &&
			self.$popup.length &&
			self.$popup.css("visibility") === "visible"
		) {
			self.adjustPopupPosition();
		}
	};
	Promise.all([fontsReady, Promise.all(imageWaiters)]).then(relayoutIfStillOpen);

	// Raise z-index of container to ensure it's above sibling popups and their dots
	this.options.$el.css('z-index', '102');

	// Enable pointer events and make popup visible
	this.$popup.css({
		visibility: "visible",
		opacity: "0",
		"pointer-events": "auto",
		transition: "none"
	});

	this._attachPopupResizeObserver();

	// Force reflow to ensure the initial state is applied
	this.$popup[0].offsetHeight;

	// Use requestAnimationFrame so the browser applies visibility (and any pending layout)
	// from this tick before we measure again — second pass fixes most first-hover glitches.
	requestAnimationFrame(function() {
		requestAnimationFrame(function() {
			self.adjustPopupPosition();
			self.$popup.css({
				transition: transitionOpacityIn,
				opacity: "1"
			});
			$card.css({
				transition: transitionMoveIn,
				transform: "translateY(0)"
			});
			$ptr.css({
				transition: transitionMoveIn,
				transform: "rotate(45deg)"
			});
		});
	});
};

/**
 * HidePopup: Fade out the popup content (or close the centered modal on mobile).
 * Class presence on the popup wrapper drives which path runs, so a modal that
 * was opened on mobile still cleans up correctly even if the breakpoint flips.
 */
BasePopup.prototype.hidePopup = function() {
	var self = this;

	if (!this.$popup || this.$popup.length === 0) return;

	if (this.$popup.hasClass('is-modal')) {
		this._hideModalPopup();
		return;
	}

	this._detachPopupResizeObserver();

	var lift = POPUP_ANIM_LIFT_PX + "px";
	var transitionOpacityOut =
		"opacity " + POPUP_ANIM_OUT_DURATION + " " + POPUP_ANIM_EASE_OUT;
	var transitionMoveOut =
		"transform " + POPUP_ANIM_OUT_DURATION + " " + POPUP_ANIM_EASE_OUT;
	var outMs = Math.round(parseFloat(POPUP_ANIM_OUT_DURATION) * 1000) + 30;

	var $card = this.$popup.find(".popup-unit_card-wrapper, .popup-item_card-wrapper");
	var $ptr = this.$popup.find(".popup-unit_pointer, .popup-item_pointer");

	// Disable pointer events immediately to prevent re-opening during fade-out
	this.$popup.css("pointer-events", "none");

	// Fade out; inner layers drift slightly down (inverse of entrance)
	this.$popup.css({
		opacity: "0",
		transition: transitionOpacityOut
	});
	$card.css({
		transform: "translateY(" + lift + ")",
		transition: transitionMoveOut
	});
	$ptr.css({
		transform: "translateY(" + lift + ") rotate(45deg)",
		transition: transitionMoveOut
	});

	// After transition completes, hide visibility, disable pointer events, and reset pointer
	setTimeout(function() {
		if (self.$popup.css("opacity") === "0") {
			self.$popup.css({
				visibility: "hidden",
				"pointer-events": "none",
				transition: "none"
			});
			$card.add($ptr).css({
				transform: "",
				transition: "none"
			});

			// Reset pointer display so it shows correctly next time
			if (self.$pointer && self.$pointer.length > 0) {
				self.$pointer.css('display', '');
			}
			
			// Reset container z-index back to default
			self.options.$el.css('z-index', '');
		}
	}, outMs);
};

/**
 * Open the popup as a fullscreen inset modal (mobile): wrapper is relocated into
 * `.availability-map_modal-host` / `.map-hotspots_modal-host` above the backdrop.
 */
BasePopup.prototype._showPopupAsModal = function() {
	var self = this;

	if (BasePopup._activeModal && BasePopup._activeModal !== this) {
		BasePopup._activeModal._closeMobileModalImmediately();
	}
	BasePopup._activeModal = this;

	this.hideOtherPopups();

	PubSub.publish('availabilityMap:syncPinPositions');

	this.resetPopupMotionTransforms();

	var $host = this._modalHost();
	if ($host.length) {
		// Clear PopupPositioner leftovers from any prior desktop hover session.
		this.$popup.css({
			left: '',
			right: '',
			top: '',
			bottom: '',
			width: '',
			height: '',
			margin: '',
			transform: ''
		});
		this.$popup.addClass('is-modal');
		this.$popup.appendTo($host);
	} else {
		// Fallback: very old markup — keep wrapper on the pin; layout will be imperfect.
		console.warn(
			(this.options.componentName || 'BasePopup') +
				': modal host not found — rebuild templates for mobile map modals.'
		);
		this.$popup.addClass('is-modal');
	}

	this._mapContainer().addClass('has-popup-open');

	this.$popup.find('img').each(function() {
		var img = this;
		if ('loading' in img && img.loading === 'lazy') {
			img.loading = 'eager';
		}
	});

	this.$popup.css({
		visibility: 'visible',
		opacity: '0',
		'pointer-events': 'auto',
		transition: 'none'
	});
	this.$popup[0].offsetHeight;

	requestAnimationFrame(function() {
		self.$popup.css({
			opacity: '1',
			transition: 'opacity ' + (MODAL_FADE_IN_MS / 1000) + 's ease-out'
		});
	});

	this._attachOutsideClose();
};

/**
 * Non-animated modal teardown when the user opens a different marker — avoids
 * orphaned wrappers stuck in the inset host.
 */
BasePopup.prototype._closeMobileModalImmediately = function() {
	if (!this.$popup || !this.$popup.length || !this.$popup.hasClass('is-modal')) {
		return;
	}
	if (this._modalCloseTimer) {
		clearTimeout(this._modalCloseTimer);
		this._modalCloseTimer = null;
	}

	this._detachOutsideClose();
	this._mapContainer().removeClass('has-popup-open');

	this.$popup.removeClass('is-modal');
	this.$popup.css({
		visibility: 'hidden',
		opacity: '0',
		'pointer-events': 'none',
		transition: 'none'
	});

	this.$popup.prependTo(this.options.$el);
	this.$popup.css({
		left: '',
		right: '',
		top: '',
		bottom: '',
		width: '',
		height: '',
		margin: '',
		transform: ''
	});

	if (BasePopup._activeModal === this) {
		BasePopup._activeModal = null;
	}
};

/**
 * Close the centered modal and tear down its document-level listener and
 * backdrop class. Runs whenever the popup wrapper carries `is-modal`,
 * regardless of current breakpoint, so a modal opened on mobile that survives
 * a resize to desktop still cleans up properly.
 */
BasePopup.prototype._hideModalPopup = function() {
	var self = this;

	if (this._modalCloseTimer) {
		clearTimeout(this._modalCloseTimer);
		this._modalCloseTimer = null;
	}

	this._detachOutsideClose();
	this._mapContainer().removeClass('has-popup-open');

	this.$popup.css({
		opacity: '0',
		'pointer-events': 'none',
		transition: 'opacity ' + (MODAL_FADE_OUT_MS / 1000) + 's ease-in'
	});

	this._modalCloseTimer = setTimeout(function() {
		self._modalCloseTimer = null;

		if (!self.$popup.hasClass('is-modal')) {
			return;
		}

		if (self.$popup.css('opacity') === '0') {
			self.$popup.css({
				visibility: 'hidden',
				transition: 'none'
			});
			self.$popup.removeClass('is-modal');
			self.$popup.prependTo(self.options.$el);
			self.$popup.css({
				left: '',
				right: '',
				top: '',
				bottom: '',
				width: '',
				height: '',
				margin: '',
				transform: ''
			});
		}

		if (BasePopup._activeModal === self) {
			BasePopup._activeModal = null;
		}
	}, MODAL_FADE_OUT_MS);
};

/**
 * Install a document-level click listener for the mobile map modal:
 * – Tap dimmed map/backdrop (non-interactive hit): fade-out dismiss via hidePopup.
 * – Tap `<a>` / `button` / `.button` inside the tooltip sheet: immediate teardown
 *   (`has-popup-open`, relocated wrapper) so CTAs can stack cleanly under `.mol-Modal`.
 * – Other taps on unrelated controls elsewhere: ignored here.
 *
 * Modal.prototype.open also calls dismissActiveMapPopupModal() so Apply Now cannot
 * leave the map overlay stranded when document listener order varies.
 *
 * Dot toggle uses stopPropagation so it won't double-fire dismiss here.
 */
BasePopup.prototype._attachOutsideClose = function() {
	var self = this;
	this._detachOutsideClose();
	this._outsideHandler = function(e) {
		var $action = $(e.target).closest('a, button, .button');

		if (
			$action.length &&
			self.$popup &&
			self.$popup.length &&
			self.$popup[0].nodeType &&
			e.target.nodeType &&
			$.contains(self.$popup[0], $action[0])
		) {
			self._closeMobileModalImmediately();
			return;
		}

		if ($action.length) {
			return;
		}

		self.hidePopup();
	};
	$(document).on('click.popupModal-' + this.instanceId, this._outsideHandler);
};

BasePopup.prototype._detachOutsideClose = function() {
	$(document).off('click.popupModal-' + this.instanceId);
	this._outsideHandler = null;
};

/**
 * Subscribe to floor change events from AvailabilityMap
 */
BasePopup.prototype.subscribeToFloorChanges = function() {
	var self = this;
	var componentName = this.options.componentName || 'BasePopup';
	
	// Subscribe to floor change events (1-based floor index)
	PubSub.subscribe('availabilityMap:floorChanged', function(msg) {
		// underpub-js passes data as the first argument (msg), not as a second parameter
		var eventData = msg && msg.data ? msg.data : msg;
		
		if (!eventData || typeof eventData.floorIndex === 'undefined') {
			if (componentName) {
				console.warn(componentName + ': floorChanged event missing floorIndex', {msg: msg, eventData: eventData});
			}
			return;
		}
		
		var activeFloorIndex = parseInt(eventData.floorIndex, 10); // 1-based
		
		// Only proceed if we have a valid floorIndex
		if (!self.floorIndex || isNaN(self.floorIndex) || isNaN(activeFloorIndex)) {
			if (componentName) {
				console.warn(componentName + ': Invalid floorIndex comparison', {
					selfFloorIndex: self.floorIndex,
					activeFloorIndex: activeFloorIndex
				});
			}
			return;
		}
		
		// Mismatch is expected - popups on different floors will not match
		// No need to log this as it's normal behavior
		
		// Check if this popup belongs to the active floor (strict comparison)
		if (self.floorIndex === activeFloorIndex) {
			// Show dot for this floor
			self.showDotForFloor();
			self.isVisibleForCurrentFloor = true;
		} else {
			// Hide dot for other floors
			// If popup is currently open, close it first
			if (self.$popup && self.$popup.css('opacity') !== '0') {
				self.hidePopup();
			}
			self.hideDotForFloor();
			self.isVisibleForCurrentFloor = false;
		}
	});
	
	// Subscribe to initial floor set event
	PubSub.subscribe('availabilityMap:initialFloorSet', function(msg) {
		// underpub-js passes data as the first argument (msg), not as a second parameter
		var eventData = msg && msg.data ? msg.data : msg;
		
		if (!eventData || typeof eventData.floorIndex === 'undefined') {
			if (componentName) {
				console.warn(componentName + ': initialFloorSet event missing floorIndex', {msg: msg, eventData: eventData});
			}
			return;
		}
		
		var activeFloorIndex = parseInt(eventData.floorIndex, 10); // 1-based
		
		// Only proceed if we have a valid floorIndex
		if (!self.floorIndex || isNaN(self.floorIndex) || isNaN(activeFloorIndex)) {
			if (componentName) {
				console.warn(componentName + ': Invalid floorIndex comparison (initial)', {
					selfFloorIndex: self.floorIndex,
					activeFloorIndex: activeFloorIndex
				});
			}
			return;
		}
		
		// Mismatch is expected - popups on different floors will not match
		// No need to log this as it's normal behavior
		
		if (self.floorIndex === activeFloorIndex) {
			self.showDotForFloor();
			self.isVisibleForCurrentFloor = true;
		} else {
			// If popup is currently open, close it first
			if (self.$popup && self.$popup.css('opacity') !== '0') {
				self.hidePopup();
			}
			self.hideDotForFloor();
			self.isVisibleForCurrentFloor = false;
		}
	});
	
	// Subscribe to current floor response (for late-initializing popups)
	PubSub.subscribe('availabilityMap:currentFloor', function(msg) {
		// underpub-js passes data as the first argument (msg), not as a second parameter
		var eventData = msg && msg.data ? msg.data : msg;
		
		if (!eventData || typeof eventData.floorIndex === 'undefined') return;
		
		var activeFloorIndex = parseInt(eventData.floorIndex, 10); // 1-based
		
		// Only proceed if we have a valid floorIndex
		if (!self.floorIndex || isNaN(self.floorIndex) || isNaN(activeFloorIndex)) return;
		
		if (self.floorIndex === activeFloorIndex) {
			self.showDotForFloor();
			self.isVisibleForCurrentFloor = true;
		} else {
			// If popup is currently open, close it first
			if (self.$popup && self.$popup.css('opacity') !== '0') {
				self.hidePopup();
			}
			self.hideDotForFloor();
			self.isVisibleForCurrentFloor = false;
		}
	});

	PubSub.subscribe('availabilityMap:pinsPositioned', function() {
		if (!self.$popup || !self.$popup.length) {
			return;
		}
		if (self.$popup.css('visibility') !== 'visible') {
			return;
		}
		var o = parseFloat(self.$popup.css('opacity'));
		if (isNaN(o) || o < 0.01) {
			return;
		}
		self.adjustPopupPosition();
	});
};

/**
 * Check the current floor by requesting it from AvailabilityMap
 * This handles the case where popups initialize after the initial floor is set
 */
BasePopup.prototype.checkCurrentFloor = function() {
	// Only check if we have a valid floorIndex
	if (!this.floorIndex || isNaN(this.floorIndex)) {
		return;
	}
	
	// Request current floor from AvailabilityMap
	PubSub.publish('availabilityMap:requestCurrentFloor');
	
	// Also check after a short delay to ensure AvailabilityMap is ready
	var self = this;
	setTimeout(function() {
		PubSub.publish('availabilityMap:requestCurrentFloor');
	}, 100);
};

/**
 * Show dot for floor visibility (without hover interaction)
 */
BasePopup.prototype.showDotForFloor = function() {
	// Clear non-active floor state first (see _popup-floor-visibility.scss)
	this.options.$el.removeClass('el-Popup--floorHidden');

	if (!this.$dot || this.$dot.length === 0) {
		if (this.options.componentName) {
			console.warn(this.options.componentName + ': Dot element not found', {
				floorIndex: this.floorIndex,
				$dot: this.$dot
			});
		}
		return;
	}
	
	// Enable pointer events on container so it can receive hover events
	this.options.$el.css('pointer-events', 'auto');
	
	// Only show if not already visible from hover
	var currentOpacity = parseFloat(this.$dot.css('opacity')) || 0;
	if (currentOpacity < 1 || this.$dot.css('display') === 'none') {
		// Set display first
		this.$dot.css('display', 'flex');
		
		// Force reflow to ensure display change is applied
		this.$dot[0].offsetHeight;
		
		// Set opacity with transition
		this.$dot.css({
			'opacity': '1',
			'transition': 'opacity 0.25s ease-out'
		});
	}
};

/**
 * Hide dot for floor visibility (without hover interaction)
 */
BasePopup.prototype.hideDotForFloor = function() {
	var self = this;
	
	// Always mark non-active floor on the root (PopupItem: Webflow can override inline styles on the icon; also hides pointer/connector)
	this.options.$el.addClass('el-Popup--floorHidden');
	// Disable pointer events on container to prevent hover events when dot is hidden
	this.options.$el.css('pointer-events', 'none');

	if (!this.$dot || this.$dot.length === 0) {
		return;
	}
	
	// Always hide the dot when switching floors (don't check popup state)
	// The popup will be hidden separately via hidePopup() if needed
	this.$dot.css({
		'opacity': '0',
		'transition': 'opacity 0.2s ease-in'
	});
	
	setTimeout(function() {
		if (self.$dot.css('opacity') === '0') {
			self.$dot.css('display', 'none');
		}
	}, 200);
};

/** Clears mobile map popup/modal shell (`has-popup-open`, relocated wrapper). */
BasePopup.dismissActiveMapPopupModal = function() {
	if (BasePopup._activeModal) {
		BasePopup._activeModal._closeMobileModalImmediately();
	}
};

export default BasePopup;

